import { useMemo } from "react";

import { useDigitalSizerClient } from "./useDigitalSizerApi";

// Correspondents are served by eastview-os (`/correspondents/*` in
// correspondents.py) AND eastview-os has open CORS, so this admin screen uses
// the LOS client. Paths below are mapped to the eastview-os routes (which differ
// from digital_sizer's original shapes). NOTE: digital-sizer-web is NOT usable
// from this app — its CORS doesn't allow the eastviewos origin.
//
// Every method below hits a route the backend serves today. Routes it does NOT
// serve have been removed rather than left as 404-on-click stubs — the one
// exception is the xlsx import pair at the bottom, whose screen is unlinked from
// the nav until the backend adds it.
export const OVERLAY_CATEGORIES = [
  "Opening / Processing",
  "Underwriting",
  "Closing",
  "General",
];

// Typed contact slots — the `contactType` path segment on /contacts/*. The two
// single-object slots (primaryContact, mlpaSignedBy) are NOT here: they are set
// via POST /{companyId}/update.
export const CONTACT_TYPES = {
  additionalContacts: "additionalContacts",
  authorizedSigners: "authorizedSigners",
  servicingContacts: "servicingContacts",
};

// Where each typed slot actually lives on the correspondent document — the path
// segment and the field name differ for signers/servicing.
export const CONTACT_SLOT_FIELDS = {
  additionalContacts: "additionalContacts",
  authorizedSigners: "correspondentAuthorizedSigners",
  servicingContacts: "servicing.servicingContacts",
};

// CorrespondentContact.role — the *correspondent* role set (not the loan
// `loanRole`). Source: the eastview-os CorrespondentContact model.
export const CORRESPONDENT_CONTACT_ROLES = [
  "Broker",
  "Correspondent",
  "Other",
  "Underwriter",
  "Sales",
];

// Where the relationship stands. Copied VERBATIM from ONBOARDING_STATUS_OPTIONS
// in the eastview-os CorrespondentCompany model — these are matched by string
// equality server-side, so the capitalisation is part of the value.
// `inactiveReason` (free text on the model) explains an Inactive one; the
// backend does not enforce it, the Profile tab does.
export const ONBOARDING_STATUSES = ["Onboarding", "Active", "Inactive"];

// Everything that can be uploaded to a correspondent — verbatim and in order
// from CORRESPONDENT_DOC_CATEGORIES in eastview-os app/models/correspondents.py.
// Hand-copied because `/model_schemas/object/{type}` doesn't serve the
// correspondent models (see SERVICER_OPTIONS in pages/Drilldown.js); re-check
// it when the model changes. 2026-10-14: Broker Agreement + the onboarding
// package (Prospect Questionnaire, Onboarding Checklist, Background
// Authorization) added.
export const DOCUMENT_CATEGORIES = [
  "Mortgage Loan Purchase Agreement",
  "Asset Management Agreement",
  "Loan Funding Agreement",
  "Broker Agreement",
  "Non-Disclosure Agreement",
  "Prospect Questionnaire",
  "Onboarding Checklist",
  "Background Authorization",
  "W9",
  "Wiring Instructions",
  "Corporate Docs",
  "Licensing",
  "Insurance",
  "Other",
];

// Only these four get the version-control machinery (auto-numbering, lineage,
// diff, current-of-record) — AGREEMENT_DOC_CATEGORIES on the model. NDA is a
// document category but is NOT versioned. Versioning is PER-CATEGORY: the AMA
// lineage numbers independently of the MLPA lineage. The legacy abbreviation
// "MLPA" is still accepted via MLPA_CATEGORY_ALIASES.
export const AGREEMENT_CATEGORIES = [
  "Mortgage Loan Purchase Agreement",
  "Asset Management Agreement",
  "Loan Funding Agreement",
  "Broker Agreement",
];

// Blank, most-recent fillable copies of the onboarding questionnaire, checklist
// and agreements live in the team's Box folder (the backend stores no
// templates) — correspondent-onboarding-checklist.md §2.
export const ONBOARDING_BLANK_FORMS_URL =
  "https://arrowls.box.com/s/2edskomj2oeg91lmx4ufemgy8rkm60gl";

// Document status workflow (POST /documents/{docId}/status). "Final Executed"
// promotes the version to the current agreement of record and stamps
// mlpaSigned on the correspondent.
export const AGREEMENT_STATUSES = [
  "Proposed",
  "Under Review",
  "Approved",
  "Final Executed",
];

// What an upload represents in the negotiation (CorrespondentDocument.agreementStage).
export const AGREEMENT_STAGES = ["Initial", "Revision", "Final Executed"];

export const docId = (d) => (d ? d._id ?? d.id ?? "" : "");
// Contacts and correspondent documents are addressed by `oid`, not `_id`.
export const oidOf = (d) => (d ? d.oid ?? d._id ?? d.id ?? "" : "");

export const useCorrespondentsApi = () => {
  const client = useDigitalSizerClient();

  return useMemo(() => {
    const get = async (p, cfg) => (await client.get(p, cfg)).data;
    const post = async (p, b, cfg) => (await client.post(p, b, cfg)).data;
    const del = async (p) => (await client.delete(p)).data;

    return {
      // ── company CRUD ────────────────────────────────────────────────────
      // eastview-os: GET /correspondents/list (no server-side filter params;
      // filter client-side). Sorted by companyName.
      list: () => get("/correspondents/list"),
      get: (id) => get(`/correspondents/${id}`),
      // GET /correspondents/{id}/loans — the correspondent's loans from BOTH
      // pipelines (pre-close + acquisitions), matched on the loan's
      // correspondentCompanyId. → { counts: { total, … }, loans: [{ …, pipeline,
      // link, outcome, keyDate, keyDateBasis, matchedBy }] }
      // (frontend_documentation/correspondent-loans-and-notes-tabs.md, 2026-10-23).
      loans: (id) => get(`/correspondents/${id}/loans`),
      // eastview-os: POST /correspondents/create
      create: (body) => post("/correspondents/create", body),
      // eastview-os: POST /correspondents/{id}/update
      update: (id, body) => post(`/correspondents/${id}/update`, body),
      // eastview-os: DELETE /correspondents/{id} → { deleted }
      remove: (id) => del(`/correspondents/${id}`),
      // eastview-os: GET /correspondents/attribute/by_email/{email} — resolve
      // which correspondent an email belongs to.
      attributeByEmail: (email) =>
        get(`/correspondents/attribute/by_email/${encodeURIComponent(email)}`),
      // eastview-os: GET /underwriting/underwriting_pipeline — the full loan
      // pipeline (no correspondent filter server-side; filter client-side by
      // `originator`). Used by the correspondent Loans tab. There's no dedicated
      // "loans for correspondent" route in correspondents.py.
      pipeline: () => get("/underwriting/underwriting_pipeline"),

      // ── typed contact CRUD ──────────────────────────────────────────────
      // `contactType` ∈ additionalContacts | authorizedSigners |
      // servicingContacts (anything else → 400). `cid` is the contact's `oid`.
      // All three return the refreshed correspondent.
      addContact: (id, contactType, body) =>
        post(`/correspondents/${id}/contacts/${contactType}`, body),
      // Partial $set — send only the changed fields.
      updateContact: (id, contactType, cid, body) =>
        post(
          `/correspondents/${id}/contacts/${contactType}/${cid}/update`,
          body,
        ),
      deleteContact: (id, contactType, cid) =>
        del(`/correspondents/${id}/contacts/${contactType}/${cid}`),

      // ── documents + agreement version control ───────────────────────────
      // eastview-os: GET /correspondents/{id}/documents → { companyId, documents }
      listDocuments: (id, category) =>
        get(
          `/correspondents/${id}/documents${
            category ? `?category=${encodeURIComponent(category)}` : ""
          }`,
        ),
      // Multipart: `submission` is a JSON string alongside the file. For the
      // MLPA family the backend auto-increments agreementVersion and defaults
      // status to "Proposed".
      // `cfg` is passed to axios — e.g. { onUploadProgress } for a progress bar.
      uploadDocument: (id, file, submission, cfg) => {
        const fd = new FormData();
        fd.append("uploadFile", file);
        fd.append("submission", JSON.stringify(submission || {}));
        return post(`/correspondents/${id}/documents/upload`, fd, cfg);
      },
      // Streams the raw bytes — pulled as a Blob so the caller can preview or
      // save it. `inline` renders in-tab rather than forcing a download.
      downloadDocument: (id, dId, { inline = false } = {}) =>
        get(
          `/correspondents/${id}/documents/${dId}/download${
            inline ? "?inline=true" : ""
          }`,
          { responseType: "blob" },
        ),
      // Body: { status, diffSummary?, agreementStage? }. "Final Executed" also
      // marks the version current + stamps mlpaSigned on the correspondent.
      setDocumentStatus: (id, dId, body) =>
        post(`/correspondents/${id}/documents/${dId}/status`, body),
      // Body: an MLPAIndexedTerms shape (or { indexedTerms: {...} }).
      indexDocumentTerms: (id, dId, body) =>
        post(`/correspondents/${id}/documents/${dId}/index_terms`, body || {}),
      // eastview-os: GET /correspondents/{id}/agreements/versions?category=
      // → { companyId, versions[], current }. Defaults to the MLPA family.
      agreementVersions: (id, category) =>
        get(
          `/correspondents/${id}/agreements/versions${
            category ? `?category=${encodeURIComponent(category)}` : ""
          }`,
        ),
      // eastview-os: GET /correspondents/{id}/agreements/diff — defaults to
      // latest vs the version it supersedes. Returns { diffAvailable: false,
      // reason } until BOTH versions have extractedText.
      agreementDiff: (id, { fromDocId, toDocId, category } = {}) => {
        const qs = new URLSearchParams();
        if (fromDocId) qs.set("fromDocId", fromDocId);
        if (toDocId) qs.set("toDocId", toDocId);
        if (category) qs.set("category", category);
        const q = qs.toString();
        return get(`/correspondents/${id}/agreements/diff${q ? `?${q}` : ""}`);
      },

      // ── onboarding checklist (2026-10-14) ───────────────────────────────
      // eastview-os: GET /correspondents/{id}/onboarding_checklist →
      // { companyId, checklist, template[], progress, auditTrail[], options }
      getOnboardingChecklist: (id) =>
        get(`/correspondents/${id}/onboarding_checklist`),
      // PATCH semantics: send only what changed (+ optional `note`, stamped on
      // every audit entry of the save). `""` = not provided, `null` clears.
      // Items go under `items: { "post_close.w9": { status, documentIds, … } }`.
      // Returns the same shape as the GET; 400 carries a showable message.
      saveOnboardingChecklist: (id, body) =>
        post(`/correspondents/${id}/onboarding_checklist`, body),

      // ── config editor (versioned rich text) ─────────────────────────────
      // eastview-os: GET /correspondents/{id}/editor →
      // { json_content, htmlString, version, versions[] }
      getEditor: (id) => get(`/correspondents/${id}/editor`),
      // Body: { json_content, htmlString?, markdown_content? }. Snapshots the
      // prior version into `versions[]`.
      saveEditor: (id, body) => post(`/correspondents/${id}/editor`, body),

      // ── pricing ─────────────────────────────────────────────────────────
      // eastview-os: GET /correspondents/{id}/pricing (no `program` param).
      effectivePricing: (id) => get(`/correspondents/${id}/pricing`),
      // eastview-os: POST /correspondents/{id}/pricing/update — terms + lock
      // settings only now (buyRate, tfFee, max_lock_days, extension fees).
      // PATCH semantics: send changed fields; '' is ignored, null clears.
      updatePricingArrangements: (cid, body) =>
        post(`/correspondents/${cid}/pricing/update`, body),
      // eastview-os: POST /correspondents/{id}/pricing/rtl — RTL/GUC fields only
      // (rtl_pricing_tier, rtl_pricing_spreads, rtl_additional_spread_pct,
      // rtl_spread_overlay_note + legacy spreads). `reason` REQUIRED; any other
      // field is a 400. Returns the pricing block (rtlEffectiveSpreads, …).
      updateRtlPricing: (cid, body) =>
        post(`/correspondents/${cid}/pricing/rtl`, body),
      // eastview-os: POST /correspondents/{id}/pricing/dscr — DSCR fields only
      // (pricingTier / dscr_pricing_tier, dscr_additional_margin,
      // dscr_margin_override_points). `reason` REQUIRED; strict fields.
      updateDscrPricing: (cid, body) =>
        post(`/correspondents/${cid}/pricing/dscr`, body),

      // ── guideline overlays ──────────────────────────────────────────────
      // eastview-os: GET /correspondents/{id}/overlays → { guidelineOverlays }
      listOverlays: (cid) => get(`/correspondents/${cid}/overlays`),
      // eastview-os overlay write routes are singular `/overlay`.
      createOverlay: (cid, body) => post(`/correspondents/${cid}/overlay`, body),
      updateOverlay: (cid, ovid, body) =>
        post(`/correspondents/${cid}/overlay/${ovid}/update`, body),
      deleteOverlay: (cid, ovid) =>
        del(`/correspondents/${cid}/overlay/${ovid}`),

      // ── global admin pricing config (Lendmarq-admin only) ───────────────
      // DSCR tier margins (dscr_tier_margins collection). Margins are in bond
      // points (par = 100). list → array of tier docs (_id = tier name).
      // eastview-os: GET /correspondents/admin/tier_margins
      listTierMargins: () => get("/correspondents/admin/tier_margins"),
      // eastview-os: GET /correspondents/admin/tier_margins/{tier}/history
      tierMarginHistory: (tier) =>
        get(
          `/correspondents/admin/tier_margins/${encodeURIComponent(tier)}/history`,
        ),
      // eastview-os: POST /correspondents/admin/tier_margins/{tier}
      // body: { marginPoints (required), label?, maxPrice?, maxPriceShortPPP?,
      //   minPrice?, minLoanAmount?, maxLoanAmount?, description?, notes? }
      upsertTierMargin: (tier, body) =>
        post(
          `/correspondents/admin/tier_margins/${encodeURIComponent(tier)}`,
          body,
        ),

      // RTL base-rate sheet (rtl_base_rates collection). Rates are annual decimal
      // fractions (0.105 = 10.50%). list → { rows:[{_id=product,...}], products }.
      // eastview-os: GET /correspondents/admin/rtl_base_rates
      listRtlBaseRates: () => get("/correspondents/admin/rtl_base_rates"),
      // eastview-os: GET /correspondents/admin/rtl_base_rates/{product}/history
      rtlBaseRateHistory: (product) =>
        get(
          `/correspondents/admin/rtl_base_rates/${encodeURIComponent(product)}/history`,
        ),
      // eastview-os: POST /correspondents/admin/rtl_base_rates/{product}
      // body: { label?, ratesByTerm: { "12": 0.105, ... }, notes? }
      upsertRtlBaseRate: (product, body) =>
        post(
          `/correspondents/admin/rtl_base_rates/${encodeURIComponent(product)}`,
          body,
        ),

      // RTL/GUC account-spread tiers (rtl_tier_spreads collection) — the
      // options for a correspondent's `rtl_pricing_tier`. spreadPct is PERCENT
      // (0.25 = 0.25%). list → array of tier docs (_id = tier name).
      // eastview-os: GET /correspondents/admin/rtl_tier_spreads → { rows }
      listRtlTierSpreads: async () => {
        const res = await get("/correspondents/admin/rtl_tier_spreads");
        return Array.isArray(res) ? res : res?.rows || [];
      },

      // ── portal users ────────────────────────────────────────────────────
      // There is NO GET /correspondents/{id}/users route. Users added through
      // add_user are persisted with `correspondentCompanyId` pinned to the
      // correspondent, so the roster comes from the full user list filtered on
      // that field (with the company's email domains as a fallback for users
      // created before the pin existed).
      listUsers: async (cid, { emailDomains = [] } = {}) => {
        const all = await get("/users/all_users");
        const rows = Array.isArray(all) ? all : (all?.users ?? []);
        const domains = emailDomains.map((d) => String(d).toLowerCase());
        return rows.filter((u) => {
          const pinned =
            u?.correspondentCompanyId ?? u?.correspondent_company_id;
          if (pinned) return String(pinned) === String(cid);
          if (!domains.length) return false;
          const email = String(u?.emailAddress ?? u?.email ?? "").toLowerCase();
          return domains.some((d) => email.endsWith(`@${d}`));
        });
      },
      // eastview-os: POST /correspondents/{id}/add_user
      createUser: (cid, body) => post(`/correspondents/${cid}/add_user`, body),

      // ⚠️ No eastview-os route — xlsx import isn't implemented. The Import
      // screen is unlinked from the nav until the backend adds these; both calls
      // 404 today.
      importPreview: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return post("/correspondents/import/preview", fd);
      },
      importApply: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return post("/correspondents/import", fd);
      },
    };
  }, [client]);
};
